import { Injectable, OnDestroy } from '@angular/core';
import { SkuCellRendererComponent } from '../ag-grid-components/sku-cell-renderer/sku-cell-renderer.component';
import { StatusBarRendererComponent } from '../ag-grid-components/status-bar-renderer/status-bar-renderer.component';
import { LoadingCellRendererComponent } from '../ag-grid-components/loading-cell-renderer/loading-cell-renderer.component';
import { CustomHeaderTooltipComponent } from '../ag-grid-components/custom-header-tooltip/custom-header-tooltip.component';
import { GridActionsService } from './grid-actions.service';
import { GridApiService } from './grid-api.service';
import { GridApi, GridOptions, GridReadyEvent } from '@ag-grid-community/core';
import { GridCheckboxHeaderRendererComponent } from '../ag-grid-components/grid-checkbox-header-renderer/grid-checkbox-header-renderer.component';
import { ShipmentNameCellRendererComponent } from '../ag-grid-components/shipment-name-cell-renderer/shipment-name-cell-renderer.component';
import { ClaimCenterToolPanelComponent } from '../ag-grid-components/claim-center-tool-panel/claim-center-tool-panel.component';
import { CaseStatusCellRendererComponent } from '../ag-grid-components/case-status-cell-renderer/case-status-cell-renderer.component';
import { ShipmentMonitorService } from '../services/shipment-monitor.service';
import { ItemListCellRendererComponent } from '../ag-grid-components/item-list-cell-renderer/item-list-cell-renderer.component';
import { SnackbarComponent } from '../components/snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { updateShipmentDiscrepancy } from '../models/shipmentMonitor.model';
import { Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ClaimCenterGridConfigService implements OnDestroy {
  gridApi: GridApi;
  isGridReady: boolean = false;
  claimStatus:any[] = [];
  private destroy$ = new Subject<void>();

  constructor(private gridActionsService: GridActionsService,
    private gridApiService: GridApiService,
    private shipmentMonitor: ShipmentMonitorService,
    private snackbar:MatSnackBar) {
      this.shipmentMonitor.getCaseStatuses().pipe(takeUntil(this.destroy$)).subscribe((res)=>{
        this.claimStatus = res ;
      });
     }

  columndefs = [
    {
      field: "",
      backendFields: [],
      checkboxSelection: true,
      width: 50,
      minWidth: 50,
      resizable: false,
      pinned: "left",
      lockPosition: true,
      suppressColumnsToolPanel: true,
      headerComponent: GridCheckboxHeaderRendererComponent,
    },
    {
      field: "Sku",
      backendFields: [
        "ImageUrl",
        "Sku", 
        "Asin",
        "Title",
        "Account",
        'ShipmentDiscrepancy'
      ],
      headerName: "Product",
      headerTooltip: "Product",
      sortable: true,
      minWidth: 300,
      width:300,
      cellRenderer: "skuCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "SKU",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ASIN",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            display: "accordion",
            title: "Description",
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
        ],
      },
    },
    {
      field: "FnSku",
      headerName: "FNSKU",
      headerTooltip: "FNSKU",
      backendFields: ["FnSku"],
      required:true,
      hide:true,
      width: 120,
      minWidth: 120,
      filter: "agTextColumnFilter",
    }, 
    {
      field: "AmazonShipmentId",
      backendFields: ["AmazonShipmentId","ShipmentName","ShipmentId"],
      headerName: "Shipping Info",
      minWidth: 180,
      width:180,  
      sortable: true,
      headerTooltip: "Shipping Info",
      filter: "agMultiColumnFilter",
      cellRenderer: "shipmentNameCellRendererComponent",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "AmazonShipmentId",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ShipmentName",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
        ],
      },
      cellRendererParams: (params) => {
        return {
          isShipmentInfo:true,
        };
      },
    },
    {
      field: "ShippedQty",
      headerName: "Qty",
      width: 120,
      sortable: true,
      backendFields: ["ShippedQty","ReceivedQty"],
      cellRenderer: "itemListCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "ShippedQty",
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "ReceivedQty",
          },
        ],
      },
      cellRendererParams: (params) => {
        return {
          props: [
            { label: "Shipped", price: params.data.ShippedQty, type: "number" },
            { label: "Received", price: params.data.ReceivedQty, type: "number" },
          ],
        };
      },
    },
    {
      field: "MissingQty",
      headerName: "Claim",
      headerTooltip:"Claim",
      sortable: true,
      backendFields: ["MissingQty","EstimatedRefundAmount"],
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "MissingQty",
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "EstimatedRefundAmount",
          },
        ],
      },
      width: 120,
      cellRenderer: "itemListCellRendererComponent",
      cellRendererParams: (params) => {
        return {
          props: [
            { label: "Missing Qty", price: params.data.MissingQty, type: "number" },
            { label: "Expected Refund", price: params.data.EstimatedRefundAmount, type: "currency" },
          ],
        };
      },
    },
    {
      headerName:'Case Status',
      field:'CaseStatusId',
      sortable: true,
      backendFields:['CaseStatusId','CaseLink','EstimatedRefundAmount'],
      minWidth:480,
      width:480,
      filter: 'agNumberColumnFilter',
      cellRendererParams: (params) => {
        return {
          status:this.claimStatus
        };
      },
      cellRenderer:'caseStatusCellRendererComponent'
    },
    {
      field: "Note",
      headerTooltip:"Notes",
      headerName:'Notes',
      sortable: true,
      editable: true,
      filter: 'agTextColumnFilter',
      backendFields: ["Note"],
      width: 250,
      onCellValueChanged: (event) =>
          this.editNote(event),
    },
  ]
  components = {
    skuCellRendererComponent: SkuCellRendererComponent,
    statusBarRendererComponent: StatusBarRendererComponent,
    loadingCellRenderer: LoadingCellRendererComponent,
    customHeaderTooltipComponent: CustomHeaderTooltipComponent,
    gridCheckboxHeaderRendererComponent: GridCheckboxHeaderRendererComponent,
    shipmentNameCellRendererComponent:ShipmentNameCellRendererComponent,
    claimCenterToolPanel:ClaimCenterToolPanelComponent,
    caseStatusCellRendererComponent:CaseStatusCellRendererComponent,
    itemListCellRendererComponent:ItemListCellRendererComponent
    
  };
  
  gridOptions: GridOptions = {
    rowModelType: "serverSide",
    blockLoadDebounceMillis: 1,
    suppressServerSideInfiniteScroll: false,
    tooltipShowDelay: 800,
    rowSelection: "multiple",
    suppressCopyRowsToClipboard: true,
    defaultColDef: {
      resizable: true,
      filterParams: {
        maxNumConditions : 1,
      },
      menuTabs: ["filterMenuTab"],
      tooltipComponent: "customHeaderTooltipComponent",
    },
    components: this.components,
    statusBar: {
      statusPanels: [
        { statusPanel: "statusBarRendererComponent", align: "left" },
      ],
    },
    sideBar: {
      toolPanels: [
        {
          id: "claimPanel",
          labelDefault: "claimPanel",
          labelKey: "claimPanel",
          iconKey: "claimPanel",
          toolPanel: "claimCenterToolPanel",
        },
      ],
    },
    rowHeight: 59,
    headerHeight: 50,
    suppressRowClickSelection: true,
    loadingCellRenderer: "loadingCellRenderer",
    cacheBlockSize: 100,
    pivotPanelShow: "onlyWhenPivoting",
    rowBuffer: 50,
    enableCellChangeFlash: true,
    suppressMenuHide: true,
    icons: {
      menu: '<span class="ag-icon ag-icon-filter" unselectable="on" role="presentation"></span>',
      filter:
        '<span class="ag-icon ag-icon-filter" style="display:none;" unselectable="on" role="presentation"></span>',
    },
    onGridReady: (e:GridReadyEvent) => {
      this.gridApi = e.api;
      this.gridApiService.gridApi$.next(e);
      this.gridActionsService.applycolumnState(e);
      this.gridApi.sizeColumnsToFit();
    },
    onColumnVisible: (e) => this.gridActionsService.columnVisible(e,this.isGridReady),
    onDragStopped: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onSortChanged: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onFirstDataRendered:() => this.isGridReady = true,
    onRowClicked: ($event) => {
      if($event.node.data && this.gridActionsService.isOpened){
        this.gridActionsService.selectedClaimResponse = $event.node.data;
        this.gridActionsService.openSidePanel('claimPanel');
        this.refreshSelectedProduct();
      }
      },
  };

  refreshSelectedProduct() {
    const toolpanelComponent = this.gridApi.getToolPanelInstance('claimPanel');
    if (toolpanelComponent) {
      toolpanelComponent.refresh(null);
    }
  }

  editNote(event){
    const paylaod:updateShipmentDiscrepancy = {
      shipmentDiscrepancyId: event.data.ShipmentDiscrepancyId,
      isCaseCreated: event.data.isCaseCreated,
      caseStatusId: event.data.CaseStatusId,
      caseLink: event.data.CaseLink ,
      note: event.data.Note,
      refundedAmount:event.data.RefundedAmount
      }
      this.shipmentMonitor.updateShipmentDiscrepancy(paylaod).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
        if(res){
          event.api.refreshServerSide();
          this.snackbar.openFromComponent(SnackbarComponent,
            {
              duration: 3000,
              horizontalPosition: 'center',
              data: {
                title: 'Status has been updated successfully',
                action: 'Dismiss',
                type: 'success',
              },
            }
          );
        }
      },
     error: (err) => {
        this.snackbar.openFromComponent(SnackbarComponent,
          {
            duration: 3000,
            horizontalPosition: 'center',
            data: {
              title: err.error?.error[0],
              action: 'Dismiss',
              type: 'error',
            },
          }
        );
       }
     });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
